import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Slide,
} from '@material-ui/core';
import { TransitionProps } from '@material-ui/core/transitions';
import { observer } from 'mobx-react-lite';
import React, { useState, useEffect } from 'react';
import CloseIcon from '@material-ui/icons/Close';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children?: React.ReactElement<any, any> },
    ref: React.Ref<unknown>,
) {
    return <Slide direction='up' ref={ref} {...props} />;
});

export const DocumentViewer: React.FC<{
    openDocViewer: boolean;
    closeDocViewer: () => void;
    doc: { title: string; url: string };
}> = observer(({ openDocViewer, closeDocViewer, doc }) => {
    const [useFallback, setUseFallback] = useState<boolean>(false);
    const [fileType, setFileType] = useState<string | null>(null);

    useEffect(() => {
        if (doc.url) {
            try {
                const urlObj = new URL(doc.url);
                const pathname = decodeURIComponent(urlObj.pathname);
                const filename = pathname.split('/').pop();
                setFileType(filename?.split('.').pop()?.toLowerCase() || null);
            } catch (error) {
                console.error('Invalid URL:', error);
                setFileType(null);
            }
        }
    }, [doc.url]);

    return (
        <Dialog
            open={openDocViewer}
            TransitionComponent={Transition}
            keepMounted
            onClose={closeDocViewer}
            aria-labelledby='alert-dialog-slide-title'
            aria-describedby='alert-dialog-slide-description'
            fullWidth
            maxWidth='lg'
        >
            <DialogTitle id='alert-dialog-slide-title'>
                <Box display='flex' justifyContent='space-between' style={{ cursor: 'pointer' }}>
                    {doc.title.replace('_', ' ')}
                    <CloseIcon style={{ cursor: 'pointer' }} onClick={closeDocViewer} />
                </Box>
            </DialogTitle>
            <DialogContent
                style={{
                    overflow: 'hidden',
                    height: '60vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {!!fileType && (
                    <>
                        {/* PDF OR DOCX Handling */}
                        {fileType === 'pdf' || fileType === 'docx' || fileType === 'doc' ? (
                            <>
                                {!useFallback ? (
                                    <iframe
                                        src={`https://docs.google.com/gview?url=${encodeURIComponent(
                                            doc.url,
                                        )}&embedded=true`}
                                        style={{
                                            border: 'none',
                                            width: '100%',
                                            height: '100%',
                                            overflow: 'hidden',
                                        }}
                                        title={doc.title}
                                        onLoad={() => setUseFallback(false)}
                                        onError={() => setUseFallback(true)}
                                    />
                                ) : (
                                    <object
                                        data={doc.url}
                                        type={
                                            fileType === 'pdf'
                                                ? 'application/pdf'
                                                : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                                        }
                                        width='100%'
                                        height='100%'
                                    >
                                        <p>
                                            Cannot display document.{' '}
                                            <a
                                                href={doc.url}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                            >
                                                Click here to download
                                            </a>
                                        </p>
                                    </object>
                                )}
                            </>
                        ) : // Image Handling
                        fileType === 'jpg' || fileType === 'jpeg' || fileType === 'png' ? (
                            <img
                                src={doc.url}
                                alt='Document'
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain',
                                }}
                            />
                        ) : (
                            <p>
                                Unsupported file type. <br />
                                <a href={doc.url} target='_blank' rel='noopener noreferrer'>
                                    Click here to view or download
                                </a>
                            </p>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button
                    variant='contained'
                    color='primary'
                    onClick={() => window.open(doc.url, '_blank', 'noopener,noreferrer')}
                >
                    View in New Tab
                </Button>
                <Button onClick={closeDocViewer}>Close</Button>
            </DialogActions>
        </Dialog>
    );
});
